import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API, api, money } from "../lib/api";
import { useCart } from "../lib/cart";

const Box = ({ children, testid }) => (
  <section className="px-5 lg:px-12 pt-20 max-w-2xl" data-testid={testid}>{children}</section>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sid) return setState({ status: "error" });
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const s = await api.paymentStatus(sid);
        if (s.payment_status === "paid") {
          if (s.items.some((i) => i.type === "merch")) clear();
          return setState({ status: "paid", data: s });
        }
        if (s.payment_status === "expired" || s.payment_status === "failed") return setState({ status: "error" });
      } catch {
        return setState({ status: "error" });
      }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState({ status: "pending" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "checking")
    return <Box testid="payment-checking"><Loader2 className="w-10 h-10 animate-spin text-lime" /><p className="mt-4 text-slate-400">Confirming your payment…</p></Box>;
  if (state.status !== "paid")
    return (
      <Box testid="payment-not-confirmed">
        <XCircle className="w-12 h-12 text-red-400" />
        <h1 className="font-cond uppercase font-black text-5xl mt-5">{state.status === "pending" ? "Still processing" : "Payment not completed"}</h1>
        <p className="text-slate-400 mt-3">{state.status === "pending" ? "Your payment is still being confirmed. Refresh in a minute." : "No charge was made."}</p>
        <Link to="/home" className="btn-line inline-block rounded-full px-6 py-3 mt-8 font-cond uppercase tracking-widest">Back home</Link>
      </Box>
    );
  return (
    <Box testid="payment-success">
      <CheckCircle2 className="w-14 h-14 text-lime" />
      <h1 className="font-gothic text-6xl mt-5">You're in</h1>
      <p className="text-slate-400 mt-3" data-testid="payment-email-note">
        Payment confirmed. {state.data.email_status === "failed" ? "We couldn't send the email right now — keep this page for your tickets." : `Your ${state.data.tickets.length ? "tickets and " : ""}receipt are on their way to ${state.data.email}.`}
      </p>
      {state.data.tickets.length > 0 && (
        <div className="card-surface rounded-xl p-6 mt-8 space-y-4" data-testid="payment-tickets">
          {state.data.tickets.map((t, i) => (
            <div key={i}>
              <p className="font-cond uppercase font-bold text-xl">{t.name}</p>
              <p className="text-xs text-slate-400 mb-2">{t.venue} · {t.date} · {t.time}</p>
              <div className="flex flex-wrap gap-2">
                {t.codes.map((c) => (
                  <figure key={c} data-testid={`ticket-code-${c}`} className="text-center">
                    <img src={`${API}/tickets/qr/${c}.png`} alt={`QR ${c}`} className="w-28 h-28 rounded-md bg-white" />
                    <figcaption className="font-mono-ui text-xs text-lime mt-1">{c}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="card-surface rounded-xl p-6 mt-8 space-y-2" data-testid="payment-summary">
        {state.data.items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm"><span>{it.quantity} × {it.name}</span><span className="font-mono-ui">{money(it.quantity * it.unit_price)}</span></div>
        ))}
        <div className="flex justify-between pt-3 border-t border-[#222634] font-cond uppercase tracking-widest text-lg"><span>Total</span><span className="text-lime">{money(state.data.amount)}</span></div>
      </div>
      <Link to="/home" data-testid="payment-home-link" className="btn-lime inline-block rounded-full px-8 py-3.5 mt-8 font-cond uppercase font-bold tracking-widest">Back home</Link>
    </Box>
  );
};

export const PaymentCancel = () => (
  <Box testid="payment-cancel">
    <h1 className="font-cond uppercase font-black text-5xl">Checkout cancelled</h1>
    <p className="text-slate-400 mt-3">No charge was made. Your cart is saved.</p>
    <Link to="/home" className="btn-line inline-block rounded-full px-6 py-3 mt-8 font-cond uppercase tracking-widest">Back home</Link>
  </Box>
);
